import { useStore } from '@nanostores/react';
import { useMemo, useRef, useState } from 'react';
import { chatId } from '~/lib/persistence/useChatHistory';
import { apiListDataDefinitions, type DataDefinition } from '~/lib/api/data';
import { classNames } from '~/utils/classNames';
import {
  BTN_SECONDARY,
  CARD,
  LoadError,
  Pill,
  Section,
  Segmented,
  SkeletonCard,
} from '~/components/workbench/tools/payments/ui';
import { useResource } from '~/components/workbench/tools/payments/use-resource';
import { canEditDefinition, namespaceLabel } from './content-model';
import { createReferenceLoader } from './references';
import { EntriesView } from './EntriesView';
import { FieldDefinitionsView } from './FieldDefinitionsView';
import { TypeEditor } from './TypeEditor';

type View = { kind: 'types' } | { kind: 'entries'; typeId: string } | { kind: 'edit-type'; typeId: string | null };

/**
 * Owner-facing Content panel: content types (metaobjects) the site reads with
 * useMetaobjects, their entries, and custom fields (metafields) on products,
 * orders, pages… The app id IS the chat id. Content changes reach the live
 * site within ~30s, no publish needed.
 */
export function ContentPanel() {
  const appId = useStore(chatId);

  return (
    <div className="relative flex h-full flex-col">
      <div className="border-b border-hairline px-4 py-3.5">
        <div className="flex items-center gap-2.5">
          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-accent/10 ring-1 ring-inset ring-accent/15">
            <div className="i-ph:database text-[13px] text-accent" />
          </div>
          <h3 className="text-[13px] font-medium tracking-tight text-primary">Content</h3>
        </div>
        <p className="mt-2 text-[11px] leading-relaxed text-muted">
          Lists your site reads, like your team, menu or FAQ. Edit them here and the site updates in about 30 seconds.
        </p>
      </div>

      {appId ? (
        <ContentPanelBody key={appId} appId={appId} />
      ) : (
        <div className="flex flex-1 items-center justify-center px-6 text-center text-[12px] text-muted">
          Start building to add content.
        </div>
      )}
    </div>
  );
}

function ContentPanelBody({ appId }: { appId: string }) {
  const [tab, setTab] = useState<'types' | 'fields'>('types');
  const [view, setView] = useState<View>({ kind: 'types' });
  const defs = useResource(() => apiListDataDefinitions(appId), appId);
  const defsRef = useRef<DataDefinition[]>([]);
  defsRef.current = defs.data ?? [];

  // One loader per panel: reference choices are fetched once and shared.
  const references = useMemo(() => createReferenceLoader(appId, () => defsRef.current), [appId]);
  const types = (defs.data ?? []).filter((d) => d.kind === 'metaobject');
  const typeById = (id: string | null) => (id ? types.find((t) => t.id === id) : undefined);

  if (view.kind === 'entries') {
    const def = typeById(view.typeId);

    if (def) {
      return (
        <EntriesView
          appId={appId}
          def={def}
          references={references}
          onBack={() => setView({ kind: 'types' })}
          onEditType={() => setView({ kind: 'edit-type', typeId: def.id })}
          onCountChange={(n) =>
            defs.setData((list) => (list ?? []).map((d) => (d.id === def.id ? { ...d, entryCount: n } : d)))
          }
        />
      );
    }
  }

  if (view.kind === 'edit-type') {
    return (
      <TypeEditor
        appId={appId}
        existing={typeById(view.typeId)}
        types={types}
        onCancel={() => setView(view.typeId ? { kind: 'entries', typeId: view.typeId } : { kind: 'types' })}
        onSaved={(saved) => {
          defs.setData((list) => {
            const rest = (list ?? []).filter((d) => d.id !== saved.id);
            return [...rest, saved];
          });
          setView({ kind: 'entries', typeId: saved.id });
        }}
        onDeleted={(id) => {
          defs.setData((list) => (list ?? []).filter((d) => d.id !== id));
          setView({ kind: 'types' });
        }}
      />
    );
  }

  return (
    <div className="flex-1 space-y-5 overflow-y-auto p-4">
      <Segmented
        label="Content view"
        value={tab}
        options={[
          ['types', 'Content types'],
          ['fields', 'Custom fields'],
        ]}
        onChange={setTab}
      />

      {tab === 'fields' ? (
        <FieldDefinitionsView appId={appId} defs={defs} />
      ) : (
        <Section
          title="Content types"
          icon="i-ph:stack"
          aside={
            <button
              type="button"
              className={BTN_SECONDARY}
              onClick={() => setView({ kind: 'edit-type', typeId: null })}
            >
              <div className="i-ph:plus text-[13px]" />
              New type
            </button>
          }
        >
          {defs.loading && !defs.data ? (
            <>
              <SkeletonCard lines={1} />
              <SkeletonCard lines={1} />
            </>
          ) : defs.error ? (
            <LoadError message="Could not load your content." onRetry={() => void defs.reload()} />
          ) : !types.length ? (
            <div className={classNames(CARD, 'space-y-2 px-4 py-6 text-center')}>
              <div className="i-ph:stack mx-auto text-xl text-faint" />
              <p className="text-[12px] leading-relaxed text-muted">
                No content types yet. Create one (Team, Menu, FAQ…) or ask Dunea in chat: "let me edit our team myself".
              </p>
            </div>
          ) : (
            <ul className="space-y-2">
              {types.map((t) => (
                <li key={t.id}>
                  <TypeRow def={t} onOpen={() => setView({ kind: 'entries', typeId: t.id })} />
                </li>
              ))}
            </ul>
          )}
        </Section>
      )}
    </div>
  );
}

function TypeRow({ def, onOpen }: { def: DataDefinition; onOpen: () => void }) {
  const from = namespaceLabel(def.namespace);
  const count = def.entryCount ?? 0;

  return (
    <button
      type="button"
      onClick={onOpen}
      className={classNames(
        CARD,
        'flex w-full items-center gap-3 px-4 py-3 text-left transition-colors duration-150 hover:border-subtle',
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-[13px] font-medium text-primary">{def.name}</span>
          {def.access.storefront !== 'public' && <Pill tone="muted" label="Private" />}
          {!canEditDefinition(def) && from && <Pill tone="info" label={from} />}
        </div>
        <div className="mt-0.5 truncate text-[11px] text-muted">
          {count === 1 ? '1 entry' : `${count} entries`} · {(def.fields ?? []).length} fields ·{' '}
          <code className="font-geist-mono">{def.key}</code>
        </div>
      </div>
      <div className="i-ph:caret-right shrink-0 text-[13px] text-muted" />
    </button>
  );
}
